import { Button, CardContent } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  EmptiesSwitch,
  EveryEmptyScope,
  FieldGrid,
  FieldGridItem,
  HelpPopover,
  NotPermitted,
  RecordSection,
  useEveryEmpty
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  ConditionOtherwise,
  IInternalFieldSource,
  IInternalFormSchema,
  IInternalSchemaField,
  IInternalUnset,
  IInternalValue,
  IInternalValueOption,
  IParticipantSurveyValues,
  InternalPayload
} from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import type { ICurrentCell } from '../../api/lists-contracts';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import type {
  IAdminParticipantDetail,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import { LoadError, Section, TableSkeleton } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import type { ISectionNavEntry } from '../../components/section-nav';
import { AdminInlineField, InternalFieldDisplay } from '../../components/field-controls';
import { LogEntryComposer, NotesLog } from '../../components/notes-log';
import { internalValueText, isInternalFieldType } from '../../internal-field-values';
import { changedWhen, REMOVED_ACCOUNT } from '../../lists';
import { isNotesLog } from '../../notes-log';
import { responsePath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { surveysOf } from './household';
import { HUB_MEMORY_PAGE } from './hub-memory';
import { ResearchHistory } from './research-history';

/**
 * Veritant research — the staff fields Salesforce shows on the advisor page, per household member
 * and per survey (the advisor record page's block F2: S1, W1, W2; the F2 plan's R1–R9, R13).
 *
 * **Which parts exist is read off the responses, which values off `internal`** (R1). A part is one
 * (member, survey) for every survey of the member's rows in `hub.responses`, the opened
 * participant first and, inside a member, by survey code. survey-api leaves a survey out of a
 * member's record when nothing is stored for it, so the record cannot say which surveys a member
 * has — only what is stored for them.
 *
 * **Drawn while the hub carries `internal` naming a member** (R2), decided from the hub document
 * before any read, as Disclosures is: the nav and the body cannot disagree, and the nav does not
 * move when a schema lands. survey-api before its SR-222 ignores `include=internal`, and the
 * section is then not drawn.
 *
 * **A part draws its survey's schema, valued from the record, verbatim** (R4, R5). The schema —
 * read once per survey per page load (`internalFieldsApi.schema`) — gives the groups, their order,
 * the labels and what may be seen; each group is a `RecordSection` (Information, U4 and DD open by
 * default, by name — U7), and a field's value is drawn as stored, a retired option labelled by the
 * value's own options. Nothing is computed from a value, and a schema that fails to load is said in
 * its part alone, the others drawn.
 *
 * **Read-only says why** (R6; Q1 ruled (a)): a field survey-api derives (BrokerCheck, figures or
 * rankings — the source in its codes, as text), and a field recorded per cycle or per response,
 * which the participant record lists without the response it belongs to, so this page could not
 * address a write to it. **A derived field whose source a block of the part holds links to it**
 * (F5's R8): "Edit in Asset & production" beside the reason, whose press asks the block to show the
 * value's place — the fold opened, the row scrolled to, the cell focused — and **only scrolls**,
 * never navigates (a fragment is a history entry without state; the section nav's rule). The part
 * holds the request and hands it to the block of that kind (`reveal`), and says beside the button
 * what the block answers (`revealed`) — "No figure as of …" or "No placement in …" where the
 * block's tables hold no such cell. A source no block of the part holds stays text.
 *
 * **Values of fields no longer offered** (R7) — a field retired, unbound or hidden since — are
 * drawn at the end of their part by their code, never dropped: the record lists them, the schema
 * does not. On a survey tab they and the condition-hidden values are the last section, "Values
 * outside the form", drawn only when it holds something.
 *
 * **A condition is evaluated by survey-api and drawn here** (R8 restated by F12's R9, its Q3 ruled
 * (a)): the record carries each value's `active` resolved, and `unset` the bound fields holding no
 * value with theirs, so a field off by its condition is drawn as its binding's `otherwise` says —
 * the one member of the raw `condition` read here, never `when`, `all`, `any` or an `op`, since
 * evaluating them would be a second evaluator beside survey-api's (CLAUDE.md's rules of the code).
 * `disable`: in place, read-only, "Off by its condition", its history kept. `hide`: not drawn — but
 * a stored value is never dropped, so a hidden field holding one is drawn read-only at the part's
 * end, "Hidden by its condition". A condition whose `otherwise` cannot be read is drawn as
 * `disable` would draw it, never hidden on a guess. A field neither valued nor in `unset` (an API
 * before it) is drawn as any field, and so is one any of whose values survey-api resolved active.
 * Every write re-reads the hub, which is how a moved `active` comes back — a write's own answer is
 * always active. **A field open in its editor keeps being drawn as editable until it is closed**
 * (gate 2, the owner's ruling): another field's write can resolve it off under the draft, and
 * taking the editor away would drop the draft and the focus unsaid; a Save sends, survey-api
 * judges, and the resolution read meanwhile applies once the editor closes. Rejected: greying it at
 * once with the draft shown beside it — a draft nobody can send.
 *
 * **A `response` value is labelled by its response** (`responseId`), its survey and cycle read off
 * `hub.responses` and the response linked, and stays read-only: no real field is response-scoped,
 * and `unset` names none, so an empty one's state is unknown.
 *
 * **A field staff may edit is edited in place** (W1, R9): `AdminInlineField`, a `PUT` to save and
 * a `DELETE` to clear, addressed by **the first response of that member and survey** in
 * `hub.responses` — a `participant_survey` value lands on the (participant, survey) through any of
 * them, and no other scope is written here (Q1). **Every write re-reads the hub**, so what a save
 * moved elsewhere comes back with it: survey-api's Quality → Travel rule needs no rule here, and
 * this page decides nothing about which option moves Travel. Until that read lands, the part holds
 * the write's answer — the `PUT`'s stored value, or nothing after a `DELETE` — over the record, an
 * overlay dropped by **a hub read begun after the write landed** (the page keeps the newest read
 * begun); a re-read that fails leaves it, under the page's "Could not refresh". Rejected: awaiting
 * the re-read before closing the field, which holds it on "Saving…" for the whole document's read
 * and says a failed re-read as a failed save. A refusal is said beside the field by status and
 * `type`, never by `title`; the two that say the page is out of date (a 404, `code-in-use`) and an
 * answer that never arrived re-read the hub as well, since the page — or the write — may have
 * moved. **A write quotes the cell's version as the pencil opened it** (SR-230, the lists design's
 * §7 — the grid's rule on the record page): `If-Match` from the value's `version`, 0 for a field
 * holding none, none at all for a value from an API that sends no version. Stale, survey-api
 * answers 409 `cell-conflict` with the cell now, said beside the field — who changed it, when and
 * to what, "since you opened it" — with the draft kept and nothing re-read; the next save quotes
 * the version that conflict said, so saving again keeps the draft over it, and a field opened
 * after a re-read quotes the re-read's. **A field emptied here stays drawn for the visit** (Q2 ruled (a), the kit's `kept`), so
 * the focus returning to its pencil has somewhere to go.
 *
 * **Every field's history is one press away** (W2, R13), beside its pencil and on every read-only
 * field too — no longer offered included, since its value is still stored and a derived field's
 * imported row has a history of its own (`research-history.tsx`).
 *
 * **Another block draws inside a part through one slot, `partSections`** (block F5's R1, its Q1
 * ruled (a)): Rankings (F6), Asset & production (F5) and Due diligence (SR-314), each an
 * `IResearchPartSection` that
 * Veritant research's entry in `hubSections()` passes, in that order. A block is drawn
 * **directly before the first schema group holding a field survey-api derives from it** — a field
 * whose `managedBy` is `derived` and whose `source.kind` is the block's `sourceKind`, the place
 * Salesforce's page holds that block — and, where no group holds one, after the last group and
 * before the values no longer offered; while the schema loads or failed, after the part's status.
 * So the block follows the staff form's order without this page knowing any group's name.
 * Rejected: a hub section of its own (Q1's (b)) — two places for one survey's staff data — and
 * each block editing `PartBody`, which would put F5 and F6 in each other's lines.
 *
 * **On a survey tab the part is the tab's run of sections** (SR-474, the record-pages design §18):
 * no "Veritant research" card — each group, each block, the disclosures (after U4) and the values
 * outside the form are level-2 sections of the tab, in this same order, under one line naming the
 * part with its "?" and "Show empty fields in every group" (SR-427). Their Jump to entries are
 * handed up (`onEntries`) as they are drawn — a block that may draw nothing (`mayHide`) once it
 * says it is drawn — so the nav and the body are one list. The household's page keeps one card,
 * each group a level-4 fold inside it.
 */

/** What the section's writes need of the page: the re-read every write ends in, which read the hub
 *  on screen came from, and the count of the page's writes (`IHubContext`). */
export interface IResearchWrites {
  reread: () => Promise<boolean>;
  hubRead: number;
  readsBegun: () => number;
  /** Moves on every write the page lands — the cue a block's own read follows, never the poll. */
  written: number;
}

/** A derived field's press, asking its block to show where the value is kept (F5's R8). */
export interface IRevealRequest {
  /** Counts the part's presses, so a second press of one field asks again. */
  id: number;
  fieldCode: string;
  source: IInternalFieldSource;
}

/** What a block drawn inside a part is handed — the part's member and survey, the page's writes,
 *  and a derived field's press waiting for this block. */
export interface IResearchPart {
  member: IAdminParticipantDetail;
  /** The part's survey — what a block reads by. Where survey-api keeps the block's data under
   *  another survey (`CANADA_FR`'s figures and rankings are `CANADA`'s), the block's read says so
   *  (`keptUnder`), and the block writes by that. */
  surveyCode: string;
  surveyName: string;
  multi: boolean;
  writes: IResearchWrites;
  /** The member's responses of the part's survey — the cycle a block's "this cycle" is. */
  responses: readonly IOversightResponseRow[];
  /** The part's staff-field schema once read — the page load's shared read — else null. */
  schema: Nullable<IInternalFormSchema>;
  /** A press of one of this block's derived fields not yet answered, or null. */
  reveal: Nullable<IRevealRequest>;
  /** The block's answer to `reveal`, once it has shown what it can: a sentence to say beside the
   *  field's button, or null for none. */
  revealed: (request: IRevealRequest, note: Nullable<string>) => void;
  /** On a survey tab (SR-474) the block is a section of the tab — a level-2 fold under this
   *  anchor, named in the nav — and says whether it draws anything (`shown`), since a survey
   *  that keeps no rankings draws no Ranking placements. Absent: a fold inside the part. */
  section?: { anchor: string; shown: (drawn: boolean) => void };
}

/** A block drawn inside each Veritant research part (see the module's comment). */
export interface IResearchPartSection {
  /** React's key among the part's blocks. */
  key: string;
  /** The block's title — what a derived field's link names, "Edit in <title>", and its nav
   *  entry's words on a survey tab. */
  title: string;
  /** The derived `source.kind` whose fields this block holds — where in the part it is drawn, and
   *  which derived fields link to it. */
  sourceKind: string;
  /** Its anchor on a survey tab, where it is a section of the tab — absent: `research-<key>`. */
  anchorOf?: (participantId: string, surveyCode: string) => string;
  /** Draws nothing for some surveys (Ranking placements, where a survey keeps no rankings): named
   *  in the nav only once it says it is drawn (`section.shown`). */
  mayHide?: boolean;
  /** The block, inside one part — every part draws every block; one with nothing to show for its
   *  survey renders nothing. */
  render: (part: IResearchPart) => React.ReactNode;
}

/** Whether the section is drawn — the hub carries a staff-field record for a member (R2). */
export function researchDrawn(
  hub: IAdminParticipantHub,
  members: IAdminParticipantDetail[]
): boolean {
  return (hub.internal ?? []).some((record) =>
    members.some((member) => member.participantId === record.participantId)
  );
}

/** The group a survey tab's disclosures follow (SR-426) — Salesforce's U4 section. */
const U4_GROUP = 'U4';

/** Groups open on a viewer's first visit, by name — the owner's Lightning state (U7). Names,
 *  not codes: a group's code is the import's derivation, its name Salesforce's heading. */
const OPEN_BY_DEFAULT = new Set(['Information', U4_GROUP, 'DD']);

/** The trailing group's title — fields bound to no group. */
const UNGROUPED = 'Other fields';

/** The section's title, and on a survey tab the bar over its sections. */
const TITLE = 'Veritant research';

/** The values a survey tab draws outside the staff form's groups, as a section of their own. */
const OUTSIDE_TITLE = 'Values outside the form';

/** A survey tab's staff-field sections (SR-474): their anchors begin with `anchor`, and the nav
 *  is told them as they are drawn. */
interface ISurveySections {
  anchor: string;
  onEntries: (entries: ISectionNavEntry[]) => void;
}

/** A group's anchor on a survey tab — its code, made safe for an `id`, under a prefix of its own so
 *  no group's code can spell a block's anchor (`research-disclosures`, `research-outside`). */
function groupAnchor(anchor: string, code: Nullable<string>): string {
  return `${anchor}-group-${(code ?? 'other').replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

/** The disclosures' anchor on a survey tab (SR-426's copy, a section of its own there). */
function disclosuresAnchor(anchor: string): string {
  return `${anchor}-disclosures`;
}

/** A member's first response of a survey as sent — the survey's name and the response a write
 *  is addressed by. Asked only for a pair `surveysOf` found, so one exists. */
function firstResponse(
  hub: IAdminParticipantHub,
  participantId: string,
  surveyCode: string
): { surveyName: string; responseId: string } {
  const row = hub.responses.find(
    (response) =>
      response.participant.participantId === participantId && response.survey.code === surveyCode
  )!;
  return { surveyName: row.survey.name, responseId: row.responseId };
}

export function ResearchSection({
  hub,
  members,
  anchor,
  writes,
  partSections = [],
  surveyCode,
  disclosures,
  onEntries
}: {
  hub: IAdminParticipantHub;
  members: IAdminParticipantDetail[];
  anchor: string;
  writes: IResearchWrites;
  /** The blocks drawn inside each part (F5's R1), in the order given. */
  partSections?: readonly IResearchPartSection[];
  /** A person's survey tab (SR-391): that survey's part alone. Absent: every part. */
  surveyCode?: string;
  /** A survey tab's copy of a member's disclosures (SR-426), drawn after the part's U4 group —
   *  else at the part's end. Absent: none (the record tab and the household's page draw the
   *  Disclosures section beside this one). */
  disclosures?: (participantId: string, surveyCode: string, anchor?: string) => React.ReactNode;
  /** A survey tab's sections (SR-474): each group, block, the disclosures and the values outside
   *  the form drawn as a section of the tab, under no "Veritant research" card, and named to the
   *  nav by this. */
  onEntries?: (entries: ISectionNavEntry[]) => void;
}) {
  const multi = members.length > 1;
  const parts = members.flatMap((member) =>
    surveysOf(hub.responses, member.participantId)
      .filter((code) => surveyCode === undefined || code === surveyCode)
      .sort()
      .map((surveyCode) => ({
        member,
        surveyCode,
        // The first of the pair's responses as sent: any of them lands a `participant_survey`
        // value in the same place (R9). The part exists because one does.
        ...firstResponse(hub, member.participantId, surveyCode),
        record: hub.internal
          ?.find((entry) => entry.participantId === member.participantId)
          ?.surveys.find((survey) => survey.surveyCode === surveyCode)
      }))
  );

  // A survey tab draws one part — `recordView` narrows the hub to the opened participant — so its
  // sections' anchors and the nav's entries are that part's alone.
  if (onEntries) {
    return (
      <>
        {parts.map((part) => (
          <ResearchPart
            key={`${part.member.participantId}:${part.surveyCode}`}
            {...part}
            responses={hub.responses}
            multi={multi}
            writes={writes}
            partSections={partSections}
            disclosures={disclosures?.(
              part.member.participantId,
              part.surveyCode,
              disclosuresAnchor(anchor)
            )}
            sections={{ anchor, onEntries }}
          />
        ))}
      </>
    );
  }

  return (
    <Section
      title={TITLE}
      anchor={anchor}
      description={
        'Veritant’s own fields for each survey, in Salesforce’s sections and words, in the order ' +
        'of the survey’s staff form (Setup → Staff fields → Forms), not the order of its ' +
        'questions. ' +
        'A field its condition turns off is greyed out, or hidden with any value it holds kept ' +
        'at the end of its survey.'
      }
    >
      <CardContent className="grid gap-8">
        {parts.map((part) => (
          <ResearchPart
            key={`${part.member.participantId}:${part.surveyCode}`}
            {...part}
            responses={hub.responses}
            multi={multi}
            writes={writes}
            partSections={partSections}
            disclosures={disclosures?.(part.member.participantId, part.surveyCode)}
          />
        ))}
      </CardContent>
    </Section>
  );
}

type SchemaRead =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; schema: IInternalFormSchema };

/** One survey's schema — the page load's shared read (R3), and "Read again" after a failure,
 *  which asks afresh since a failed read is not kept. */
function useSchema(surveyCode: string): { read: SchemaRead; retry: () => void } {
  const [attempt, setAttempt] = React.useState(0);
  const [read, setRead] = React.useState<SchemaRead>({ status: 'loading' });
  React.useEffect(() => {
    let live = true;
    setRead({ status: 'loading' });
    internalFieldsApi.schema(surveyCode).then(
      (schema) => {
        if (live) setRead({ status: 'loaded', schema });
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', error });
      }
    );
    return () => {
      live = false;
    };
  }, [surveyCode, attempt]);
  return { read, retry: () => setAttempt((count) => count + 1) };
}

function ResearchPart({
  member,
  surveyCode,
  surveyName,
  responseId,
  record,
  responses,
  multi,
  writes,
  partSections,
  disclosures,
  sections
}: {
  member: IAdminParticipantDetail;
  surveyCode: string;
  surveyName: string;
  responseId: string;
  record: IParticipantSurveyValues | undefined;
  responses: readonly IOversightResponseRow[];
  multi: boolean;
  writes: IResearchWrites;
  partSections: readonly IResearchPartSection[];
  disclosures: React.ReactNode;
  /** A survey tab's: its sections' anchors start with `anchor`, and the nav is told them. */
  sections?: ISurveySections;
}) {
  const { read, retry } = useSchema(surveyCode);
  // On a survey tab, which blocks said whether they draw anything (a survey keeping no rankings).
  const [shown, setShown] = React.useState<ReadonlyMap<string, boolean>>(() => new Map());
  const blockAnchor = (block: IResearchPartSection) =>
    block.anchorOf?.(member.participantId, surveyCode) ?? `research-${block.key}`;
  const blockEntries = (blocks: readonly IResearchPartSection[]): ISectionNavEntry[] =>
    blocks
      .filter((block) => shown.get(block.key) ?? !block.mayHide)
      .map((block) => ({ anchor: blockAnchor(block), label: block.title }));
  // A derived field's press, waiting for its block, and what each block said back, by field.
  const [reveal, setReveal] = React.useState<Nullable<IRevealRequest>>(null);
  const [notes, setNotes] = React.useState<ReadonlyMap<string, string>>(() => new Map());
  const presses = React.useRef(0);
  const links: IFieldLinks = {
    titleOf: (source) =>
      partSections.find((block) => source !== null && block.sourceKind === source.kind)?.title ??
      null,
    press: (fieldCode, source) => {
      presses.current += 1;
      setReveal({ id: presses.current, fieldCode, source });
      setNotes((said) => {
        const next = new Map(said);
        next.delete(fieldCode);
        return next;
      });
    },
    noteOf: (fieldCode) => notes.get(fieldCode) ?? null
  };
  const revealed = (request: IRevealRequest, note: Nullable<string>) => {
    setReveal((waiting) => (waiting?.id === request.id ? null : waiting));
    if (note !== null) setNotes((said) => new Map(said).set(request.fieldCode, note));
  };
  const drawBlock = (block: IResearchPartSection) => (
    <React.Fragment key={block.key}>
      {block.render({
        member,
        surveyCode,
        surveyName,
        multi,
        writes,
        responses,
        schema: read.status === 'loaded' ? read.schema : null,
        reveal: reveal?.source.kind === block.sourceKind ? reveal : null,
        revealed,
        section: sections && {
          anchor: blockAnchor(block),
          shown: (drawn) =>
            setShown((said) =>
              said.get(block.key) === drawn ? said : new Map(said).set(block.key, drawn)
            )
        }
      })}
    </React.Fragment>
  );

  // Before the schema is in, a survey tab names the one section saying so, and the blocks.
  const onEntries = sections?.onEntries;
  const statusEntries: ISectionNavEntry[] = sections
    ? [
        { anchor: sections.anchor, label: TITLE },
        ...blockEntries(partSections),
        ...(read.status === 'failed' && disclosures != null
          ? [{ anchor: disclosuresAnchor(sections.anchor), label: 'Disclosures' }]
          : [])
      ]
    : [];
  const statusKey = JSON.stringify(statusEntries);
  React.useEffect(() => {
    if (onEntries && read.status !== 'loaded') onEntries(statusEntries);
  }, [onEntries, read.status, statusKey]);

  const status = (
    <>
      {read.status === 'loading' && <TableSkeleton rows={3} />}
      {read.status === 'failed' && (
        <div className="grid justify-items-start gap-2">
          <LoadError
            message={refusalMessage(read.error, `The staff fields of ${surveyName} could not be read.`)}
          />
          <Button type="button" size="sm" variant="outline" onClick={retry}>
            Read again
          </Button>
        </div>
      )}
    </>
  );
  const body = read.status === 'loaded' && (
    <PartBody
      blocks={partSections}
      drawBlock={drawBlock}
      links={links}
      schema={read.schema}
      surveyCode={surveyCode}
      surveyName={surveyName}
      responseId={responseId}
      record={record}
      responses={responses}
      participantId={member.participantId}
      name={member.displayName}
      writes={writes}
      disclosures={disclosures}
      sections={sections && { ...sections, blockEntries }}
    />
  );

  return (
    <div
      data-research={`${member.participantId}:${surveyCode}`}
      className={sections ? 'grid gap-6' : 'grid gap-2'}
    >
      {/* A survey tab's sections stand under the tab's own heading; while the schema is out, one
          section stands in for them. */}
      {sections ? (
        read.status !== 'loaded' && (
          <Section title={TITLE} anchor={sections.anchor}>
            <CardContent>{status}</CardContent>
          </Section>
        )
      ) : (
        <>
          <h3 className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-[var(--color-royal-blue)]">
            {surveyName}
            {multi && (
              <span className="font-normal text-muted-foreground">{member.displayName}</span>
            )}
          </h3>
          {status}
        </>
      )}
      {read.status !== 'loaded' && partSections.map(drawBlock)}
      {/* Not while the schema is read: drawn in one place and moved under U4 as it lands, the copy
          would remount and lose its switch. */}
      {read.status === 'failed' && disclosures}
      {body}
    </div>
  );
}

/** What the record holds for one field: its cycle-less values (one, or one per response for a
 *  `response` field) and its values per ranking cycle. */
interface IHeld {
  values: IInternalValue[];
  cycles: { cycleName: string; value: IInternalValue }[];
}

function heldFor(record: IParticipantSurveyValues | undefined, code: string): IHeld {
  return {
    values: (record?.values ?? []).filter((value) => value.field === code),
    cycles: (record?.cycles ?? []).flatMap((cycle) =>
      cycle.values
        .filter((value) => value.field === code)
        .map((value) => ({ cycleName: cycle.cycleName, value }))
    )
  };
}

/** The labels every value held carries for its codes — a retired option's included. */
function heldOptions(held: IHeld): IInternalValueOption[] {
  return [...held.values, ...held.cycles.map(({ value }) => value)].flatMap(
    (value) => value.options
  );
}

function isHeld(held: IHeld): boolean {
  return held.values.length + held.cycles.length > 0;
}

/** The version a write of this field quotes as drawn: the stored value's, 0 for a field holding
 *  none (survey-api's empty cell), or undefined for a value read from an API that sends no
 *  version, which then sends no `If-Match`. Only a `participant_survey` field is written here, and
 *  it holds one cycle-less value at most. */
function versionOf(held: IHeld): number | undefined {
  const stored = held.values[0];
  return stored === undefined ? 0 : stored.version;
}

/** How a field is drawn as survey-api resolved its condition (R9): `on`, as any field; `off`, in
 *  place and read-only; `hidden`, out of its group, its stored values at the part's end. */
type Resolution = 'on' | 'off' | 'hidden';

/** A schema field's `otherwise`, or null where its condition carries none this app reads — the one
 *  member of a condition read here. */
function otherwiseOf(field: IInternalSchemaField): Nullable<ConditionOtherwise> {
  const { condition } = field;
  if (typeof condition !== 'object' || condition === null) return null;
  const otherwise = (condition as { otherwise?: unknown }).otherwise;
  return otherwise === 'hide' || otherwise === 'disable' ? otherwise : null;
}

/** Off only where survey-api resolved the field — a value's `active`, or its `unset` entry's — and
 *  resolved it inactive every time; a field it said nothing about is drawn as any field. */
function resolutionOf(
  field: IInternalSchemaField,
  held: IHeld,
  unset: readonly IInternalUnset[] | undefined
): Resolution {
  const said = [
    ...held.values.flatMap((value) => (value.active === undefined ? [] : [value.active])),
    ...(unset ?? []).filter((entry) => entry.field === field.code).map((entry) => entry.active)
  ];
  if (said.length === 0 || said.some(Boolean)) return 'on';
  return otherwiseOf(field) === 'hide' ? 'hidden' : 'off';
}

/** A `response` value's response among the hub's rows — undefined where the hub carries none. */
type ResponseOf = (responseId: string) => IOversightResponseRow | undefined;

/** A write's answer, held over the record until a hub read begun after it lands (R9): the value as
 *  stored, or null for a clear; `after` is how many hub reads had begun when it landed. */
interface IWritten {
  value: Nullable<IInternalValue>;
  after: number;
}

/** The overlay applied, while the hub on screen is no later than the write. Only a
 *  `participant_survey` field is written here, and it holds one cycle-less value at most. */
function overlaid(held: IHeld, written: IWritten | undefined, hubRead: number): IHeld {
  if (written === undefined || hubRead > written.after) return held;
  return { values: written.value === null ? [] : [written.value], cycles: held.cycles };
}

/** Whether a failed write leaves the page to be read again: an answer that never arrived (the
 *  write may have landed), and the two refusals that say the page is out of date. */
function rereadsAfter(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status >= 500) return true;
  return error.status === 404 || error.isProblem(ProblemType.codeInUse);
}

/** A refusal in words, by status and `type` (R9). An answer that never arrived is the kit's to
 *  say; anything not named here is `refusalMessage`'s — a 400's `problems[]` among them. */
function refusalOf(error: unknown, surveyName: string, field: IInternalSchemaField): string {
  if (error instanceof ApiError) {
    if (error.isProblem(ProblemType.cellConflict)) return conflictOf(error, field);
    if (error.isProblem(ProblemType.fieldManaged)) {
      return 'This field is derived and cannot be edited here.';
    }
    if (error.isProblem(ProblemType.codeInUse)) {
      return `This field is no longer offered for ${surveyName} — the page is out of date.`;
    }
    // Bodiless: `internal_fields.write` taken under the open page, or a field of `admin` access
    // the viewer's roles lack `internal_fields.restricted` for — this side cannot tell which.
    if (error.status === 403) return 'Your Team Portal roles do not include changing this field.';
    if (error.status === 404) return 'The page is out of date — read it again.';
  }
  return refusalMessage(error, 'The value could not be saved.');
}

/** The cell a 409 `cell-conflict` says it is now, or null where its body carries none. */
function currentOf(error: ApiError): Nullable<ICurrentCell> {
  const current = error.problem?.current;
  if (typeof current !== 'object' || current === null) return null;
  const { version } = current as { version?: unknown };
  return typeof version === 'number' ? (current as ICurrentCell) : null;
}

/** The stored value a conflict names, in the field's words — null for an empty cell, a value
 *  this app cannot read, or a long or rich text, which a sentence does not quote. */
function conflictValue(v: unknown, field: IInternalSchemaField): Nullable<string> {
  if (typeof v !== 'object' || v === null) return null;
  const { t } = v as { t?: unknown };
  if (typeof t !== 'string' || !isInternalFieldType(t) || t === 'long_text' || t === 'rich_text') {
    return null;
  }
  return internalValueText(v as InternalPayload, field.options);
}

/** §7's sentence: "Changed by Elena Duarte at 10:41 to “5 - Average” since you opened it. Reload
 *  the page to take it, or save again to keep yours." — who by the account's email (an account
 *  since removed where it has none, nobody named where survey-api records nobody). */
function conflictOf(error: ApiError, field: IInternalSchemaField): string {
  const current = currentOf(error);
  const after = ' since you opened it. Reload the page to take it, or save again to keep yours.';
  if (current === null) return `This field was changed${after}`;
  const by = current.setBy === null ? '' : ` by ${current.setBy.email ?? REMOVED_ACCOUNT}`;
  const at = current.setAt === null ? '' : ` ${changedWhen(current.setAt)}`;
  if (current.v === null) return `Cleared${by}${at}${after}`;
  const value = conflictValue(current.v, field);
  return `Changed${by}${at}${value === null ? '' : ` to “${value}”`}${after}`;
}

/** Whether a group holds a field survey-api derives from a block of `sourceKind` — where that
 *  block is drawn (F5's R1). */
function holdsDerived(group: IInternalFormSchema['groups'][number], sourceKind: string): boolean {
  return group.fields.some(
    (field) => field.managedBy === 'derived' && field.source?.kind === sourceKind
  );
}

function PartBody({
  blocks,
  drawBlock,
  links,
  schema,
  surveyCode,
  surveyName,
  responseId,
  record,
  responses,
  participantId,
  name,
  writes,
  disclosures,
  sections
}: {
  blocks: readonly IResearchPartSection[];
  drawBlock: (block: IResearchPartSection) => React.ReactNode;
  links: IFieldLinks;
  schema: IInternalFormSchema;
  surveyCode: string;
  surveyName: string;
  responseId: string;
  record: IParticipantSurveyValues | undefined;
  responses: readonly IOversightResponseRow[];
  participantId: string;
  name: string;
  writes: IResearchWrites;
  disclosures: React.ReactNode;
  /** A survey tab's (SR-474): each group a section of the tab, named to the nav. */
  sections?: ISurveySections & {
    blockEntries: (blocks: readonly IResearchPartSection[]) => ISectionNavEntry[];
  };
}) {
  const { reread, hubRead, readsBegun } = writes;
  // By field code: the part is one (participant, survey), so this is R9's
  // (participantId, surveyCode, fieldCode).
  const [written, setWritten] = React.useState<ReadonlyMap<string, IWritten>>(() => new Map());
  // The fields emptied on this visit, which stay drawn (Q2).
  const [cleared, setCleared] = React.useState<ReadonlySet<string>>(() => new Set());
  // The fields open in their editor (editing or saving), which keep being drawn as editable until
  // closed, whatever a re-read since resolved for them (gate 2, the owner's ruling).
  const [open, setOpen] = React.useState<ReadonlySet<string>>(() => new Set());
  // The version each open editor's write quotes, by field code: the value's as the pencil opened
  // it, and after a `cell-conflict` the version the conflict said (§7). Never drawn, so a ref.
  const basis = React.useRef(new Map<string, number | undefined>());
  const openChangeOf = (code: string, version: number | undefined) => (isOpen: boolean) => {
    if (!isOpen) basis.current.delete(code);
    else if (!basis.current.has(code)) basis.current.set(code, version);
    setOpen((held) => {
      if (held.has(code) === isOpen) return held;
      const next = new Set(held);
      if (isOpen) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  // Holds the answer, then re-reads — in that order, so the re-read is a read begun after it.
  // The count is taken here, not inside the updater: React runs an updater at the next render
  // when the part already has an update queued (a clear's `setCleared`), by which time the
  // re-read has begun and the overlay would outlive it.
  const land = (code: string, value: Nullable<IInternalValue>) => {
    const after = readsBegun();
    setWritten((held) => new Map(held).set(code, { value, after }));
    void reread();
  };

  const send = async <T,>(code: string, call: () => Promise<T>): Promise<T> => {
    try {
      return await call();
    } catch (error) {
      // Saving again keeps the draft over the cell as the conflict found it (§7).
      if (error instanceof ApiError && error.isProblem(ProblemType.cellConflict)) {
        const current = currentOf(error);
        if (current !== null && basis.current.has(code)) basis.current.set(code, current.version);
      }
      // Not survey-api's answer: a dropped connection, or this side's own fault — said to staff
      // as an answer that did not arrive, and kept here for whoever debugs it.
      if (!(error instanceof ApiError)) console.error(error);
      if (rereadsAfter(error)) void reread();
      throw error;
    }
  };

  const writeOf = (code: string) => async (payload: InternalPayload) => {
    const version = basis.current.get(code);
    land(code, await send(code, () => internalFieldsApi.set(responseId, code, payload, version)));
  };
  const appendOf = (code: string) => async (text: string) => {
    land(code, await send(code, () => internalFieldsApi.appendEntry(responseId, code, text)));
  };
  const clearOf = (code: string) => async () => {
    const version = basis.current.get(code);
    await send(code, () => internalFieldsApi.clear(responseId, code, version));
    setCleared((held) => new Set(held).add(code));
    land(code, null);
  };

  // A value's write and clear are `internal_fields.write` (SR-228), apart from the read that draws
  // the section: without it every pencil is drawn off, under one sentence per part.
  const permitted = useCan()('internal_fields.write');
  const reasonId = React.useId();

  const offered = new Set(schema.groups.flatMap((group) => group.fields.map((f) => f.code)));
  const unoffered = [
    ...new Set([
      ...(record?.values ?? []).map((value) => value.field),
      ...(record?.cycles ?? []).flatMap((cycle) => cycle.values.map((value) => value.field))
    ])
  ].filter((code) => !offered.has(code));
  // Each block before the first group deriving from it, else after the last group (R1).
  const placeOf = (block: IResearchPartSection) => {
    const at = schema.groups.findIndex((group) => holdsDerived(group, block.sourceKind));
    return at === -1 ? schema.groups.length : at;
  };
  const blocksAt = (index: number) =>
    blocks.filter((block) => placeOf(block) === index).map(drawBlock);
  const responseOf: ResponseOf = (id) => responses.find((row) => row.responseId === id);
  // The disclosures go under the U4 group — by name, as the open-by-default groups are — and at
  // the part's end where the staff form has none.
  const u4At = schema.groups.findIndex((group) => group.name === U4_GROUP);

  // Each group's fields, what each holds and how its condition resolved (R9): a hidden field
  // leaves its group, and what it holds is drawn at the part's end.
  const groups = schema.groups.map((group) =>
    group.fields.map((field) => {
      const held = overlaid(heldFor(record, field.code), written.get(field.code), hubRead);
      const resolution = open.has(field.code) ? 'on' : resolutionOf(field, held, record?.unset);
      return { field, held, resolution };
    })
  );
  const hidden = groups
    .flat()
    .filter((entry) => entry.resolution === 'hidden' && isHeld(entry.held));
  // One switch for the part's every group (SR-427), drawn while any group drawn has an empty field.
  const every = useEveryEmpty(HUB_MEMORY_PAGE, `research.${surveyCode}`);
  // A group whose every field its condition hides draws nothing, as survey-api omits a group
  // holding no field the caller may see.
  const drawnOf = groups.map((entries) => entries.filter((entry) => entry.resolution !== 'hidden'));
  const anyEmpty = drawnOf.some((drawn) => drawn.some((entry) => !isHeld(entry.held)));
  const outside = hidden.length > 0 || unoffered.length > 0;

  // The nav's entries on a survey tab, in the order drawn: each place's blocks, then its group
  // and, after U4, the disclosures.
  const disclosuresEntry =
    sections && disclosures != null
      ? [{ anchor: disclosuresAnchor(sections.anchor), label: 'Disclosures' }]
      : [];
  const entries = sections && [
    ...schema.groups.flatMap((group, groupIndex) => [
      ...sections.blockEntries(blocks.filter((block) => placeOf(block) === groupIndex)),
      ...(drawnOf[groupIndex].length > 0
        ? [{ anchor: groupAnchor(sections.anchor, group.code), label: group.name ?? UNGROUPED }]
        : []),
      ...(groupIndex === u4At ? disclosuresEntry : [])
    ]),
    ...sections.blockEntries(blocks.filter((block) => placeOf(block) === schema.groups.length)),
    ...(u4At === -1 ? disclosuresEntry : []),
    ...(outside ? [{ anchor: `${sections.anchor}-outside`, label: OUTSIDE_TITLE }] : [])
  ];
  const entriesKey = JSON.stringify(entries ?? null);
  const onEntries = sections?.onEntries;
  React.useEffect(() => {
    if (onEntries && entries) onEntries(entries);
  }, [onEntries, entriesKey]);

  // Kept while on, even with no empty field left: on, it hides every group's own switch, and
  // nothing else could turn it off.
  const everySwitch = (anyEmpty || every.show) && (
    <EmptiesSwitch
      checked={every.show}
      onCheckedChange={every.set}
      label={`Show empty fields in every group of ${surveyName}`}
    >
      Show empty fields in every group
    </EmptiesSwitch>
  );
  const outsideValues = (
    <>
      {hidden.length > 0 && (
        <HiddenByCondition
          entries={hidden}
          responseOf={responseOf}
          participantId={participantId}
          name={name}
        />
      )}
      {unoffered.length > 0 && (
        <Unoffered
          codes={unoffered}
          record={record}
          responseOf={responseOf}
          surveyName={surveyName}
          participantId={participantId}
          name={name}
        />
      )}
    </>
  );

  return (
    <>
      {schema.groups.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No staff fields are set up for {surveyName}.
        </p>
      )}
      {!permitted && schema.groups.length > 0 && (
        <NotPermitted id={reasonId} what="changing staff fields" />
      )}
      {sections ? (
        <div
          data-research-bar
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm"
        >
          <span className="flex items-center gap-1 font-medium">
            {TITLE}
            <HelpPopover label={`About ${TITLE}`}>
              <p>
                Veritant’s own fields for {surveyName}, in Salesforce’s sections and words — in the
                order of its staff form (Setup → Staff fields → Forms), not the order of its
                questions, which its Answers follow.
              </p>
              <p>
                A field its condition turns off is greyed out, or hidden with any value it holds
                kept under {OUTSIDE_TITLE}.
              </p>
            </HelpPopover>
          </span>
          {everySwitch}
        </div>
      ) : (
        everySwitch && <div className="text-sm">{everySwitch}</div>
      )}
      <EveryEmptyScope show={every.show} used={every.used}>
        <div className={sections && 'grid gap-6'}>
          {schema.groups.map((group, groupIndex) => {
            const drawn = drawnOf[groupIndex];
            return (
              <React.Fragment key={group.code ?? '_'}>
                {blocksAt(groupIndex)}
                {drawn.length > 0 && (
                  <RecordSection
                    page={HUB_MEMORY_PAGE}
                    sectionKey={`research.${surveyCode}.${group.code ?? '_'}`}
                    title={group.name ?? UNGROUPED}
                    level={sections ? 2 : 4}
                    anchor={sections && groupAnchor(sections.anchor, group.code)}
                    defaultOpen={group.name !== null && OPEN_BY_DEFAULT.has(group.name)}
                    filled={drawn.filter((entry) => isHeld(entry.held)).length}
                    total={drawn.length}
                  >
                    <FieldGrid>
                      {drawn.map(({ field, held, resolution }) => (
                        <FieldGridItem
                          key={field.code}
                          label={field.name}
                          filled={isHeld(held)}
                          kept={cleared.has(field.code)}
                          wide={field.type === 'long_text' || field.type === 'rich_text'}
                        >
                          <ResearchField
                            field={field}
                            held={held}
                            off={resolution === 'off'}
                            responseOf={responseOf}
                            participantId={participantId}
                            name={name}
                            write={writeOf(field.code)}
                            append={appendOf(field.code)}
                            clear={clearOf(field.code)}
                            refusal={(error) => refusalOf(error, surveyName, field)}
                            links={links}
                            onOpenChange={openChangeOf(field.code, versionOf(held))}
                            notPermitted={permitted ? undefined : reasonId}
                          />
                        </FieldGridItem>
                      ))}
                    </FieldGrid>
                  </RecordSection>
                )}
                {groupIndex === u4At && disclosures}
              </React.Fragment>
            );
          })}
          {blocksAt(schema.groups.length)}
          {u4At === -1 && disclosures}
          {sections && outside && (
            <RecordSection
              page={HUB_MEMORY_PAGE}
              // A colon, which no group's code holds: `research.<survey>.<code>` is a group's key.
              sectionKey={`research.${surveyCode}:outside`}
              title={OUTSIDE_TITLE}
              level={2}
              defaultOpen
              filled={0}
              total={0}
              anchor={`${sections.anchor}-outside`}
            >
              {outsideValues}
            </RecordSection>
          )}
        </div>
      </EveryEmptyScope>
      {!sections && outsideValues}
    </>
  );
}

/** Why a field is read-only here, in words — or null for a field staff edit on this page. */
function readOnlyReason(field: IInternalSchemaField): Nullable<string> {
  // survey-api keeps the code `brokercheck`, but since its SR-396 the value is read off the
  // pair's answers — BrokerCheck's or IAPD's — so the words name neither (the owner's Q1).
  if (field.managedBy === 'brokercheck') return 'Derived from the registry lookups';
  if (field.managedBy === 'derived') return derivedReason(field.source);
  if (field.managedBy !== null) return `Derived from ${field.managedBy}`;
  if (field.scope === 'participant_cycle') {
    return 'Recorded per cycle — edited on the response, not here';
  }
  if (field.scope === 'response') return 'Recorded per response — edited on the response, not here';
  if (field.scope !== 'participant_survey') return `Recorded per ${field.scope} — not edited here`;
  if (!isInternalFieldType(field.type)) return `A ${field.type} field — not edited here`;
  return null;
}

/** A derived field's source in its codes — the words stay whether or not a block of the part
 *  links to it (`IFieldLinks`): the link is beside them, never instead. */
function derivedReason(source: Nullable<IInternalFieldSource>): string {
  if (source === null) return 'Derived from figures or rankings';
  if (source.kind === 'figure') {
    const parts = [source.metric, source.scope, source.asOf && `as of ${formatDay(source.asOf)}`];
    return `Derived from Asset & production: ${joined(parts)}`;
  }
  if (source.kind === 'ranking') {
    return `Derived from Ranking placements: ${joined([source.list, source.year?.toString(), source.part])}`;
  }
  return `Derived from ${source.kind}`;
}

function joined(parts: (Nullable<string> | undefined)[]): string {
  return parts.filter((part): part is string => !!part).join(', ');
}

/** A part's links from a derived field to the block holding its source (F5's R8). */
interface IFieldLinks {
  /** The title of the part's block holding `source`, or null where none does. */
  titleOf: (source: Nullable<IInternalFieldSource>) => Nullable<string>;
  press: (fieldCode: string, source: IInternalFieldSource) => void;
  /** What the block said back to the field's last press, or null. */
  noteOf: (fieldCode: string) => Nullable<string>;
}

/** One schema field: edited in place where staff may (R9), else read with its reason (R6, Q1) —
 *  and a derived one linking to the block that holds its source, where the part draws one. */
function ResearchField({
  field,
  held,
  off,
  responseOf,
  participantId,
  name,
  write,
  append,
  clear,
  refusal,
  links,
  onOpenChange,
  notPermitted
}: {
  field: IInternalSchemaField;
  held: IHeld;
  /** Off by its condition, drawn in place (R9). */
  off: boolean;
  responseOf: ResponseOf;
  participantId: string;
  name: string;
  write: (payload: InternalPayload) => Promise<void>;
  /** A signed entry at the head of a notes log (SR-494). */
  append: (text: string) => Promise<void>;
  clear: () => Promise<void>;
  refusal: (error: unknown) => string;
  links: IFieldLinks;
  /** Told as the field's editor opens and closes. */
  onOpenChange: (open: boolean) => void;
  /** The id of the part's `NotPermitted` sentence; present, the pencil is drawn off. */
  notPermitted?: string;
}) {
  const reason = withCondition(readOnlyReason(field), off ? OFF : null);
  const source = field.managedBy === 'derived' ? field.source : null;
  const linkTitle = links.titleOf(source);
  const linkNote = links.noteOf(field.code);
  const actions = (
    <FieldActions field={field} held={held} participantId={participantId} name={name} />
  );
  if (reason === null && isInternalFieldType(field.type)) {
    const stored = held.values[0] ?? null;
    // A notes log draws as its entries, their initials named on hover, with *Add entry* under it
    // (SR-494); its pencil still edits the whole text.
    const log = field.type === 'long_text' && isNotesLog(field.code);
    const text = stored?.value?.v;
    const inline = (
      <AdminInlineField
        field={{ code: field.code, label: field.name, type: field.type, options: field.options }}
        name={name}
        value={stored?.value ?? null}
        valueOptions={stored?.options}
        write={write}
        clear={clear}
        refusal={refusal}
        actions={actions}
        display={log && typeof text === 'string' && text.trim() !== '' ? <NotesLog notes={text} /> : undefined}
        onOpenChange={onOpenChange}
        notPermitted={notPermitted}
      />
    );
    if (!log) return inline;
    return (
      <div className="grid gap-1">
        {inline}
        <LogEntryComposer append={append} notPermitted={notPermitted} />
      </div>
    );
  }
  return (
    <ReadingField
      code={field.code}
      label={field.name}
      name={name}
      reason={reason}
      actions={actions}
      link={
        source !== null && linkTitle !== null ? (
          <div className="grid justify-items-start gap-1">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => links.press(field.code, source)}
            >
              Edit in {linkTitle}
            </Button>
            {linkNote !== null && <p className="text-xs">{linkNote}</p>}
          </div>
        ) : undefined
      }
    >
      <HeldValues held={held} schemaOptions={field.options} responseOf={responseOf} />
    </ReadingField>
  );
}

/** The reason a field off by its condition says (R9). */
const OFF = 'Off by its condition';
/** The reason a hidden field holding a value says, at the part's end (R9). */
const HIDDEN = 'Hidden by its condition';

/** A field's read-only reason with its condition's beside it — the condition's first, since it is
 *  the one a write here would not change. */
function withCondition(reason: Nullable<string>, condition: Nullable<string>): Nullable<string> {
  if (condition === null) return reason;
  return reason === null ? condition : `${condition} · ${reason}`;
}

/** A schema field's help and history, beside its value wherever it is drawn. */
function FieldActions({
  field,
  held,
  participantId,
  name
}: {
  field: IInternalSchemaField;
  held: IHeld;
  participantId: string;
  name: string;
}) {
  return (
    <>
      {field.helpText && <HelpText name={field.name} text={field.helpText} />}
      <ResearchHistory
        participantId={participantId}
        code={field.code}
        label={field.name}
        name={name}
        fieldType={field.type}
        options={[...heldOptions(held), ...field.options]}
      />
    </>
  );
}

/** What a field holds, drawn: one value as it reads; several each on a line — a cycle's named by
 *  the cycle (Q1), a response's by its response, linked (R9); none, the empty field's dash. */
function HeldValues({
  held,
  schemaOptions = [],
  responseOf
}: {
  held: IHeld;
  schemaOptions?: readonly IInternalValueOption[];
  responseOf: ResponseOf;
}) {
  const display = (value: IInternalValue) => (
    <InternalFieldDisplay payload={value.value} options={[...value.options, ...schemaOptions]} />
  );
  if (!isHeld(held)) return <InternalFieldDisplay payload={null} />;
  const labelled = held.values.some((value) => value.responseId !== undefined);
  if (held.values.length === 1 && held.cycles.length === 0 && !labelled) {
    return display(held.values[0]);
  }
  return (
    <ul className="grid gap-1">
      {held.values.map((value, index) => (
        <li key={`v${index}`} className="flex flex-wrap items-baseline gap-x-1.5">
          {value.responseId !== undefined && (
            <ResponseLabel responseId={value.responseId} responseOf={responseOf} />
          )}
          {display(value)}
        </li>
      ))}
      {held.cycles.map(({ cycleName, value }, index) => (
        <li key={`c${index}`} className="flex flex-wrap items-baseline gap-x-1.5">
          {/* The space is the text's (a spec reads it); the gap is what is seen. */}
          <span className="text-muted-foreground">{`${cycleName}: `}</span>
          {display(value)}
        </li>
      ))}
    </ul>
  );
}

/** A `response` value's response, by its survey and cycle as the hub's rows name it and linked to
 *  it; by its id where the hub carries no such row. The space is the text's, as a cycle's. */
function ResponseLabel({ responseId, responseOf }: { responseId: string; responseOf: ResponseOf }) {
  const row = responseOf(responseId);
  return (
    <span className="text-muted-foreground">
      <Link className="underline" to={responsePath(responseId)}>
        {row ? `${row.survey.name} ${row.cycle.name}` : `Response ${responseId}`}
      </Link>
      {': '}
    </span>
  );
}

/**
 * A field as `InlineField` reads it — the same group, name and hooks (`[data-field]`,
 * `[data-state="reading"]`, `[data-readonly]`) and the reason in visible text — for what this page
 * does not edit: a field holding several values (a cycle's, a response's), one derived, one off or
 * hidden by its condition, and a value of a field no longer offered. One shape for every read-only
 * field, rather than the kit's own read-only state for the single-valued ones, so the reason sits in one place whatever the kind.
 */
function ReadingField({
  code,
  label,
  name,
  reason,
  actions,
  link,
  children
}: {
  code: string;
  label: string;
  name: string;
  reason: Nullable<string>;
  actions?: React.ReactNode;
  /** Drawn after the reason — a derived field's way to its block. */
  link?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      role="group"
      aria-label={`${label} for ${name}`}
      data-field={code}
      data-state="reading"
      data-readonly={reason !== null || undefined}
      className="grid min-w-0 gap-1.5 rounded-sm"
    >
      <div className="flex min-w-0 items-start gap-1">
        <div className="min-w-0 flex-1 wrap-anywhere">{children}</div>
        {actions}
      </div>
      {reason !== null && <p className="text-xs text-muted-foreground">{reason}</p>}
      {link}
    </div>
  );
}

/** Salesforce's inline help for a field, one press away — never a `title`, which neither a
 *  keyboard nor a screenshot reaches. */
function HelpText({ name, text }: { name: string; text: string }) {
  return (
    <HelpPopover label={`About ${name}`} className="text-foreground">
      <p className="whitespace-pre-line">{text}</p>
    </HelpPopover>
  );
}

/** Stored values of fields their condition hides (R9) — at the part's end, by name, read-only,
 *  their history kept: a stored value is never dropped. */
function HiddenByCondition({
  entries,
  responseOf,
  participantId,
  name
}: {
  entries: { field: IInternalSchemaField; held: IHeld }[];
  responseOf: ResponseOf;
  participantId: string;
  name: string;
}) {
  const headingId = React.useId();
  return (
    <section aria-labelledby={headingId} className="grid gap-3 border-t border-border py-3">
      <h4 id={headingId} className="text-sm font-medium text-[var(--color-royal-blue)]">
        Values of fields hidden by their conditions
      </h4>
      <FieldGrid>
        {entries.map(({ field, held }) => (
          <FieldGridItem key={field.code} label={field.name} filled>
            <ReadingField
              code={field.code}
              label={field.name}
              name={name}
              reason={withCondition(readOnlyReason(field), HIDDEN)}
              actions={
                <FieldActions field={field} held={held} participantId={participantId} name={name} />
              }
            >
              <HeldValues held={held} schemaOptions={field.options} responseOf={responseOf} />
            </ReadingField>
          </FieldGridItem>
        ))}
      </FieldGrid>
    </section>
  );
}

/** Stored values of fields the schema no longer offers (R7), by code, read by their own type. */
function Unoffered({
  codes,
  record,
  responseOf,
  surveyName,
  participantId,
  name
}: {
  codes: string[];
  record: IParticipantSurveyValues | undefined;
  responseOf: ResponseOf;
  surveyName: string;
  participantId: string;
  name: string;
}) {
  const headingId = React.useId();
  return (
    <section aria-labelledby={headingId} className="grid gap-3 border-t border-border py-3">
      <h4 id={headingId} className="text-sm font-medium text-[var(--color-royal-blue)]">
        Values of fields no longer offered
      </h4>
      <FieldGrid>
        {codes.map((code) => {
          const held = heldFor(record, code);
          return (
            <FieldGridItem key={code} label={code} filled>
              <ReadingField
                code={code}
                label={code}
                name={name}
                reason={`No longer offered for ${surveyName}`}
                actions={
                  // No schema field, so no type: a history value is read by its own `t`.
                  <ResearchHistory
                    participantId={participantId}
                    code={code}
                    label={code}
                    name={name}
                    fieldType={null}
                    options={heldOptions(held)}
                  />
                }
              >
                <HeldValues held={held} responseOf={responseOf} />
              </ReadingField>
            </FieldGridItem>
          );
        })}
      </FieldGrid>
    </section>
  );
}
